import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CreditCard, Shield, Globe, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PaymentTypes } from '@/api/client';

const PaymentMethodSelector = ({ 
  selectedMethod, 
  onMethodSelect, 
  currency = 'RUB', 
  region = 'RU',
  amount = 0 
}) => {
  const { t, i18n } = useTranslation();
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPaymentTypes();
  }, [currency, region]);

  const loadPaymentTypes = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const data = await PaymentTypes.list({ currency, region });
      setPaymentTypes(data);
      
      // Автоматически выбираем первый доступный метод, если ничего не выбрано
      if (!selectedMethod && data.length > 0) {
        onMethodSelect(data[0].code);
      }
    } catch (error) {
      console.error('Error loading payment types:', error);
      setError(t('payments.messages.error_loading'));
    } finally {
      setIsLoading(false);
    }
  };

  const getProviderIcon = (provider) => {
    switch (provider) {
      case 'tbank':
        return <Shield className="w-5 h-5 text-blue-600" />;
      case 'paypal':
        return <Globe className="w-5 h-5 text-blue-500" />;
      default:
        return <CreditCard className="w-5 h-5 text-gray-600" />;
    }
  };

  const getProviderName = (provider) => {
    switch (provider) {
      case 'tbank':
        return t('payments.providers.tbank');
      case 'paypal':
        return t('payments.providers.paypal');
      default:
        return t(`payments.providers.${provider}`) || provider;
    }
  };

  const formatAmount = (amount, currency) => {
    const formatter = new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    });
    return formatter.format(amount);
  };

  const getFeatureBadges = (features) => {
    if (!features) return [];
    
    const featureLabels = {
      sbp: t('payments.features.sbp'),
      tpay: t('payments.features.tpay'),
      cards: t('payments.features.cards'),
      mirpay: t('payments.features.mirpay'),
      qr_code: t('payments.features.qr_code'),
      quick_payment: t('payments.features.quick_payment'),
      iframe_enabled: t('payments.features.iframe_enabled'),
      '3d_secure': t('payments.features.3d_secure'),
      installments: t('payments.features.installments'),
      paypal_credit: t('payments.features.paypal_credit'),
      guest_checkout: t('payments.features.guest_checkout'),
      express_checkout: t('payments.features.express_checkout')
    };

    return Object.entries(features)
      .filter(([_, enabled]) => enabled)
      .map(([key, _]) => featureLabels[key] || key)
      .slice(0, 3); // Показываем только первые 3 функции
  };

  if (isLoading) {
    return (
      <Card>
                 <CardContent className="flex items-center justify-center py-8">
           <Loader2 className="w-6 h-6 animate-spin mr-2" />
           {t('payments.messages.loading_payment_methods')}
         </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (paymentTypes.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
                 <AlertDescription>
           {t('payments.messages.no_payment_methods', { currency, region })}
         </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-amber-600" />
          {t('payments.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <RadioGroup 
          value={selectedMethod} 
          onValueChange={onMethodSelect}
          className="space-y-3"
        >
          {paymentTypes.map((type) => (
            <div key={type.code} className="flex items-start space-x-3">
              <RadioGroupItem value={type.code} id={type.code} className="mt-3" />
              <Label
                htmlFor={type.code}
                className="flex-1 min-w-0 cursor-pointer"
              >
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="shrink-0">{getProviderIcon(type.provider)}</div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900 break-words">
                        {type.name}
                      </div>
                      <div className="text-sm text-slate-600 break-words">
                        {type.description}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {getFeatureBadges(type.features).map((badge, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {badge}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="md:text-right shrink-0">
                    <div className="text-sm text-slate-600">
                      {getProviderName(type.provider)}
                    </div>
                    {amount > 0 && (
                      <div className="text-lg font-semibold text-slate-900">
                        {formatAmount(amount, currency)}
                      </div>
                    )}
                  </div>
                </div>
              </Label>
            </div>
          ))}
        </RadioGroup>
        
        {selectedMethod && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                       <div className="text-sm text-amber-800">
             <strong>{t('payments.messages.selected_method', { method: paymentTypes.find(t => t.code === selectedMethod)?.name })}</strong>
           </div>
           <div className="text-xs text-amber-600 mt-1">
             {t('payments.messages.redirect_notice')}
           </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentMethodSelector;
